import React from 'react';
import { Checkbox, Grid, Typography, Box } from '@mui/material';
import type { AccessType, ModulePermission } from './SecurityRoleMatrix.types';
import {
	applyLockedPermissions,
	isPermissionCheckboxDisabled,
} from './securityRolePermissionLocks';

type Props = {
	data: ModulePermission[];
	onChange: (updated: ModulePermission[]) => void;
	readonly?: boolean;
};

const ACCESS_LABELS: Record<AccessType, string> = {
	view: 'View',
	edit: 'Edit',
	delete: 'Delete',
	execute: 'Execute',
};

export const SecurityRoleMatrix: React.FC<Props> = ({ data, onChange, readonly }) => {
	const handleCheckChange = (modIdx: number, funcIdx: number, access: AccessType) => {
		if (isPermissionCheckboxDisabled(data[modIdx].functions[funcIdx].name, access, readonly)) {
			return;
		}

		const updatedData = [...data];
		const currentPermission = updatedData[modIdx].functions[funcIdx].permissions[access];
		const current = currentPermission === true;
		const newValue = !current;

		// Define permission hierarchy for view/edit/delete (execute is independent)
		const permissionHierarchy: AccessType[] = ['view', 'edit', 'delete'];

		if (access === 'execute') {
			// Execute is independent: just toggle it without touching other permissions
			updatedData[modIdx].functions[funcIdx].permissions.execute = newValue;
		} else {
			const currentIndex = permissionHierarchy.indexOf(access);

			if (newValue) {
				// When checking a permission, check all lower permissions
				for (let i = 0; i <= currentIndex; i++) {
					updatedData[modIdx].functions[funcIdx].permissions[permissionHierarchy[i]] = true;
				}
			} else {
				// When unchecking a permission, uncheck all higher permissions (within view/edit/delete)
				for (let i = currentIndex; i < permissionHierarchy.length; i++) {
					updatedData[modIdx].functions[funcIdx].permissions[permissionHierarchy[i]] = false;
				}
			}
		}

		onChange(applyLockedPermissions(updatedData));
	};
	return (
		<Box sx={{ paddingLeft: 1, marginTop: 2 }}>
			{data.map((mod, modIdx) => (
				<Box key={mod.module} mb={4}>
					<Grid container spacing={1} sx={{ marginLeft: '10px' }}>
						<Grid item xs={4}>
							<Typography variant="h6" gutterBottom>
								{mod.module}
							</Typography>
						</Grid>
						{Object.keys(ACCESS_LABELS).map((access) => (
							<Grid item xs={1.5} key={access}>
								<strong>{ACCESS_LABELS[access as AccessType]}</strong>
							</Grid>
						))}
					</Grid>
					{mod.functions.map((func, funcIdx) => (
						<Grid
							container
							spacing={1}
							key={func.name}
							alignItems="center"
							py={0.5}
							sx={{ marginLeft: '10px' }}
						>
							<Grid item xs={4}>
								{func.name}
							</Grid>
							{Object.keys(ACCESS_LABELS).map((access) => {
								const accessKey = access as AccessType;
								const isDisabled = isPermissionCheckboxDisabled(func.name, accessKey, readonly);

								return (
									<Grid item xs={1.5} key={access}>
										<Checkbox
											checked={func.permissions[accessKey] === true}
											disabled={isDisabled}
											onChange={() => handleCheckChange(modIdx, funcIdx, accessKey)}
										/>
									</Grid>
								);
							})}
						</Grid>
					))}
				</Box>
			))}
		</Box>
	);
};
